import {
  Button,
  Heading,
  Icon,
  LoadingIcon,
  Stack,
  Text,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

// In-flight fill state only. The finished PDF persists as a document and is
// rendered by PdfDocumentTabContent, so there's no 'ready' state here.
export type PdfFillState =
  | { status: 'preparing'; templateId: string; title: string }
  | { status: 'error'; templateId: string; title: string; message?: string };

export type PdfFillTabContentProps = {
  fill: PdfFillState;
  onStop: () => void;
  onRetry: () => void;
};

export const PdfFillTabContent: React.FC<PdfFillTabContentProps> = ({
  fill,
  onStop,
  onRetry,
}) => {
  const { t } = useTranslation();
  if (fill.status === 'preparing') {
    return (
      <Stack fullHeight justify="center" align="center" gap={4}>
        <Stack align="center" gap={4}>
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-bg-tertiary">
            <Icon name="FileText" size="2xl" className="text-text-secondary" />
          </div>
          <Stack align="center" gap={2}>
            <Heading size="xl">
              {t('appointmentTools.documents.preparingPdf')}
            </Heading>
            <Text color="secondary" className="text-center">
              {t('appointmentTools.documents.pdfFromSession')}
            </Text>
            <Text size="sm" color="tertiary" className="text-center">
              {t('appointmentTools.documents.switchTabsHint')}
            </Text>
          </Stack>
          <Stack align="center" gap={2}>
            <Button disabled>
              <LoadingIcon />
              {t('appointmentTools.documents.preparingPdfButton')}
            </Button>
            <Button variant="ghost" size="sm" onClick={onStop}>
              {t('appointmentTools.documents.stopGenerating')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack fullHeight justify="center" align="center" gap={4}>
      <Stack align="center" gap={4}>
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-bg-tertiary">
          <Icon name="AlertCircle" size="2xl" className="text-text-negative" />
        </div>
        <Stack align="center" gap={2}>
          <Heading size="xl">
            {t('appointmentTools.documents.fillError')}
          </Heading>
          {fill.message && (
            <Text color="secondary" className="text-center">
              {fill.message}
            </Text>
          )}
        </Stack>
        <Stack align="center" gap={2}>
          <Button onClick={onRetry}>
            <Icon name="RotateCcw" size="auto" />
            {t('appointmentTools.common.tryAgain')}
          </Button>
          <Button variant="ghost" size="sm" onClick={onStop}>
            {t('appointmentTools.common.close')}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
